import React, { Fragment, useMemo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import _get from "lodash/get";
import Tooltip from "@material-ui/core/Tooltip";

import { BWGrid, GridTable } from "../../../../ui/BWGrid";
import propTypes from "../../../../../constants/propTypes";
import Date from "../../../../ui/BWGrid/columns/Date";
import DeleteSubmittalButton from "./DeleteSubmittalButton";
import ActionRow from "./ActionRow";
import {
  GridWrapper,
  CellComponent,
  HeaderCellComponent,
  Check,
  VerticalAlignWrapper,
} from "./components";
import LinesEllipsis from "../../../../ui/LinesEllipsis";
import styled from "styled-components";
import SubmittalDescriptions, {
  hasDescriptionChanges,
} from "../../../SpecDetail/RequirementsSection/ReviewSubmittals/SubmittalDescriptions";
import sortSubmittalFiles from "../../../SpecDetail/RequirementsSection/ReviewSubmittals/sortSubmittalFiles";
import ReviewActions from "../../../SpecDetail/RequirementsSection/ReviewSubmittals/ReviewActions";

const columns = ["file.filename", "createdAt", "approved", "actions"];
export const Anchor = styled(Text)`
  color: #57abff;
  text-decoration: none;
`;
export const renderFilename = row => {
  const file = _get(row, "file");
  const filename = _get(row, "file.filename");
  const url = _get(file, "s3SignedUrl");
  return (
    <VerticalAlignWrapper>
      <Anchor as="a" href={url} target="_blank">
        <LinesEllipsis width="100px">{filename}</LinesEllipsis>
      </Anchor>
    </VerticalAlignWrapper>
  );
};

export const getColumnOptions = (
  specRequirement,
  { onApproveSubmittal, onDeleteSubmittal, disabled }
) => {
  const renderApprovedColumn = row => {
    const isApproved = row.id === specRequirement.submittalApprovedId;
    const approvedId = isApproved ? null : row.id;

    const onClick = disabled
      ? null
      : () => onApproveSubmittal(specRequirement.id, approvedId);

    return (
      <VerticalAlignWrapper>
        {/* Check drops the ref Tooltip needs to anchor, so a span holds it */}
        <Tooltip title={isApproved ? "Remove Approval" : "Approve"}>
          <span style={{ display: "inline-flex" }}>
            <Check
              disabled={disabled}
              isApproved={isApproved}
              onClick={onClick}
            />
          </span>
        </Tooltip>
      </VerticalAlignWrapper>
    );
  };

  const renderActionsColumn = row => {
    const { __expanded__, __isClicked__, ...submittal } = row;
    if (disabled) {
      return null;
    }
    // A file can't be edited: to change one, upload the new file and delete the old
    return (
      <DeleteSubmittalButton
        submittalFile={submittal}
        onSubmit={onDeleteSubmittal}
      />
    );
  };

  return {
    "file.filename": { title: "Filename", render: renderFilename },
    createdAt: {
      title: "Date Uploaded",
      render: ({ createdAt }) =>
        createdAt && <Date date={createdAt} format="YYYY-MM-DD" />,
    },
    approved: {
      title: " ",
      render: renderApprovedColumn,
    },
    actions: {
      title: " ",
      render: renderActionsColumn,
    },
  };
};

const Table = ({
  onApproveSubmittal,
  onDeleteSubmittal,
  onDeleteRequirement,
  onDisplayUploadFormClick,
  onUpdateRequirement,
  row: specRequirement,
  disabled,
}) => {
  const submittalFiles = useMemo(
    () => sortSubmittalFiles(_get(specRequirement, "submittalFiles")),
    [specRequirement]
  );

  const [receivedDescription, setReceivedDescription] = useState(
    specRequirement?.receivedDescription || ""
  );
  const [approvedDescription, setApprovedDescription] = useState(
    specRequirement?.approvedDescription || ""
  );

  const handleUpdate = useCallback(
    () =>
      onUpdateRequirement(specRequirement.id, {
        receivedDescription,
        approvedDescription,
      }),
    [
      onUpdateRequirement,
      specRequirement.id,
      receivedDescription,
      approvedDescription,
    ]
  );

  const hasChanges = hasDescriptionChanges(
    { receivedDescription, approvedDescription },
    specRequirement
  );

  const columnOptions = useMemo(
    () =>
      getColumnOptions(specRequirement, {
        onApproveSubmittal,
        onDeleteSubmittal,
        disabled,
      }),
    [specRequirement, onApproveSubmittal, onDeleteSubmittal, disabled]
  );

  const handleDisplayUploadFormClick = useCallback(
    () => onDisplayUploadFormClick(specRequirement),
    [onDisplayUploadFormClick, specRequirement]
  );

  return (
    <Fragment>
      <GridWrapper>
        <BWGrid
          rows={submittalFiles}
          gridConfig={{ pageSize: 0, totalRows: submittalFiles.length }}
          tableComponents={{ CellComponent, HeaderCellComponent }}
        >
          <GridTable columns={columns} columnOptions={columnOptions} />
        </BWGrid>
      </GridWrapper>

      <SubmittalDescriptions
        receivedDescription={receivedDescription}
        approvedDescription={approvedDescription}
        onReceivedDescriptionChange={setReceivedDescription}
        onApprovedDescriptionChange={setApprovedDescription}
        divider={false}
      />

      <ReviewActions hasChanges={hasChanges} onUpdate={handleUpdate} />

      <ActionRow
        onDeleteRequirement={onDeleteRequirement}
        onDisplayUploadFormClick={handleDisplayUploadFormClick}
        specRequirement={specRequirement}
      />
    </Fragment>
  );
};

Table.propTypes = {
  row: PropTypes.shape({
    submittalFiles: PropTypes.arrayOf(propTypes.submittalFile),
  }),
  onApproveSubmittal: PropTypes.func.isRequired,
  onDeleteSubmittal: PropTypes.func.isRequired,
  onDeleteRequirement: PropTypes.func.isRequired,
  onDisplayUploadFormClick: PropTypes.func.isRequired,
  onUpdateRequirement: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default Table;
